import {
  Backdrop,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Fade,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  List,
  ListItemButton,
  Modal,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import WO2Button from 'src/common/components/button/Button';
import { Container, Draggable, DropResult } from '@dash/react-smooth-dnd';
import { Close } from '@mui/icons-material';
import { PreDefinedTag, SetPreDefinedTagValue, SwapTagRequest } from '../../store/types';
import { PRE_DEFINED_TAGS } from '../../constants';

interface Props {
  handleClose: () => void;
  onConfirm?: () => void;
  isOpen: boolean;
  appendNamingTemplate: (tag: PreDefinedTag) => void;
  clearNamingTemplate: () => void;
  deleteNamingTemplateTag: (index: number) => void;
  swapNamingTemplateTags: (request: SwapTagRequest) => void;
  setNamingTemplateTagValue: (request: SetPreDefinedTagValue) => void;
  namingTemplate: PreDefinedTag[];
}

export const ReportGenerationCustomNamingModal = (props: Props): JSX.Element => {
  const theme = useTheme();

  const updateTagValue = (index: number, value: string) => {
    props.setNamingTemplateTagValue({ index: index, value: value });
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={props.isOpen}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClose={() => {
        props.handleClose();
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={props.isOpen}>
        <Card
          elevation={0}
          sx={{
            padding: theme.spacing(1),
            width: {
              xs: '90%',
              sm: '80%',
              md: '70%',
              lg: '60%',
            },
            zIndex: theme.zIndex.modal + 10,
            maxWidth: 800,
            minWidth: 300,
            height: {
              xs: 'auto',
              sm: 'auto',
            },
            overflowY: 'hidden',
          }}
        >
          <CardHeader title={<Typography variant="h3">Set Naming Template</Typography>} />
          <Divider />
          <CardContent>
            <Grid container spacing={2} pb={4}>
              <Grid item xs={12} sm={6}>
                <List dense disablePadding sx={{ overflowY: 'auto', maxHeight: 400 }}>
                  {PRE_DEFINED_TAGS.map((definedTag, index) => (
                    <ListItemButton key={index} onClick={() => props.appendNamingTemplate(definedTag)} disableGutters>
                      {definedTag.tag}
                    </ListItemButton>
                  ))}
                </List>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Container
                  style={{
                    gap: theme.spacing(1),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                  }}
                  dragHandleSelector=".drag-handle"
                  lockAxis="y"
                  onDrop={(dropResult: DropResult) => {
                    const { removedIndex, addedIndex } = dropResult;
                    props.swapNamingTemplateTags({ from: Number(removedIndex), to: Number(addedIndex) });
                  }}
                >
                  {props.namingTemplate.map((selected, index) => (
                    <Draggable key={index}>
                      {selected.tag === 'Variable' ? (
                        <Input
                          onChange={(e) => updateTagValue(index, e.target.value)}
                          fullWidth
                          value={selected.value ?? ''}
                          className="drag-handle"
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => props.deleteNamingTemplateTag(index)}>
                                <Close />
                              </IconButton>
                            </InputAdornment>
                          }
                        />
                      ) : (
                        <Chip
                          className="drag-handle"
                          label={selected.tag}
                          onDelete={() => props.deleteNamingTemplateTag(index)}
                          sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}
                          variant="filled"
                        />
                      )}
                    </Draggable>
                  ))}
                </Container>
              </Grid>
            </Grid>
          </CardContent>
          {props.namingTemplate.length > 0 && <Divider />}
          <CardActions>
            {props.namingTemplate.length > 0 && (
              <Grid container direction="column" spacing={1}>
                <Grid item>
                  <Typography variant="h4">Example</Typography>
                </Grid>
                <Grid item>
                  <Typography>
                    {props.namingTemplate.map((defined) => defined.value ?? defined.example).join('_')}
                  </Typography>
                </Grid>
                <Grid item container spacing={2} justifyContent="flex-end">
                  <Grid item>
                    <WO2Button variant="outlined" onClick={props.clearNamingTemplate}>
                      Reset
                    </WO2Button>
                  </Grid>
                  <Grid item>
                    <WO2Button
                      variant="contained"
                      onClick={() => {
                        if (props.onConfirm) {
                          props.onConfirm();
                        }
                        props.handleClose();
                      }}
                    >
                      Confirm
                    </WO2Button>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </CardActions>
        </Card>
      </Fade>
    </Modal>
  );
};
